import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CollaboratorForm } from '@/features/Collaborators/Edit/Contexts/FullForm';
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { useCollaboratorByIdQuery, useUpdateCollaboratorMutation, usePartialUpdateCollaboratorMutation } from '@/features/Collaborators/Data';
import { useVacationDpEditMode, VACATION_DP_EDITABLE_FIELDS } from '@/features/Collaborators/hooks/useVacationDpEditMode';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useToast } from '@/hooks/useToast';
import { cleanUndefinedFields } from '@/utils/firebaseUtils';

const CollaboratorEditPage = () => {
    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.debug('[edit] page mount');
        }
    }, []);

    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { success, error: toastError } = useToast();

    // Buscar dados do colaborador se for edição
    const {
        data: collaborator,
        isLoading,
        isError,
        error
    } = useCollaboratorByIdQuery(id);

    // Mutação para atualizar colaborador
    const updateCollaboratorMutation = useUpdateCollaboratorMutation();
    const partialUpdateMutation = usePartialUpdateCollaboratorMutation();
    const { isVacationDpOnly } = useVacationDpEditMode();

    const handleSubmit = async (data: CollaboratorFormData) => {
        console.log('🔵 [CollaboratorEditPage] handleSubmit CHAMADO', { id, data, isVacationDpOnly });

        try {
            // Garantir que o ID está presente nos dados
            const collaboratorData = { ...data, id: id || data.id };
            console.log('🔵 [CollaboratorEditPage] collaboratorData com ID:', collaboratorData);

            // Limpar campos undefined para evitar erro do Firebase
            const cleanedData = cleanUndefinedFields(collaboratorData);

            // Campos de controle (apenas UI) não devem ser enviados para o backend
            // photoUrl também fica de fora: a foto é trocada na hora pelo "Trocar foto" do
            // cabeçalho (AvatarEditavel) — o formulário tem a foto de quando abriu e, ao salvar,
            // voltaria pra foto antiga.
            const {
                salaryHistoryEditingId,
                salaryHistoryEditingIsSaved,
                salaryHistoryEditingUnsavedIndex,
                photoUrl,
                ...payload
            } = cleanedData as any;
            console.log('🔵 [CollaboratorEditPage] cleanedData:', cleanedData);

            // Se for DP, fazer partial update só com os campos permitidos
            if (isVacationDpOnly) {
                const partialData = {} as Record<string, any>;
                VACATION_DP_EDITABLE_FIELDS.forEach((field) => {
                    if (field in payload) {
                        partialData[field] = (payload as any)[field];
                    }
                });
                console.log('🔵 [CollaboratorEditPage] Iniciando partial update...', partialData);
                await partialUpdateMutation.mutateAsync({ id: id!, data: partialData as any });
            } else {
                console.log('🔵 [CollaboratorEditPage] Iniciando mutação de update completo...');
                await updateCollaboratorMutation.mutateAsync(payload as CollaboratorFormData);
            }
            console.log('🔵 [CollaboratorEditPage] Mutação concluída com sucesso');

            success('Colaborador atualizado!', 'Dados salvos com sucesso');
        } catch (error) {
            console.error('❌ Erro ao salvar colaborador:', error);
            toastError('Erro ao salvar colaborador', 'Tente novamente em alguns instantes');
            throw error; // Re-throw para que o formulário saiba que houve erro
        }
    };

    const handleBack = () => {
        // Navigate back to the list preserving the previous state
        const returnUrl = location.state?.from || '/collaborators/updates';
        navigate(returnUrl);
    };

    // Loading state
    if (id && isLoading) {
        return (
            <>
                <button
                    onClick={handleBack}
                    className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 mb-6"
                >
                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                    Voltar
                </button>
                <div className="bg-white shadow sm:rounded-lg mb-8">
                    <div className="px-4 py-5 sm:p-6">
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                            <span className="ml-2 text-gray-500">Carregando dados do colaborador...</span>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Error state
    if (id && isError) {
        return (
            <>
                <button
                    onClick={handleBack}
                    className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 mb-6"
                >
                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                    Voltar
                </button>
                <div className="bg-white shadow sm:rounded-lg mb-8">
                    <div className="px-4 py-5 sm:p-6">
                        <div className="text-center py-12">
                            <div className="text-red-600 mb-4">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 8.5c-.77.833-.231 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao carregar colaborador</h3>
                            <p className="text-gray-500 mb-4">
                                {error instanceof Error ? error.message : 'Não foi possível carregar os dados do colaborador.'}
                            </p>
                            <button
                                onClick={() => window.location.reload()}
                                className="btn-primary"
                            >
                                Tentar novamente
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Se for edição e colaborador não foi encontrado
    if (id && !collaborator) {
        return (
            <>
                <button
                    onClick={handleBack}
                    className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 mb-6"
                >
                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                    Voltar
                </button>
                <div className="bg-white shadow sm:rounded-lg mb-8">
                    <div className="px-4 py-5 sm:p-6">
                        <div className="text-center py-12">
                            <div className="text-yellow-600 mb-4">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 8.5c-.77.833-.231 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Colaborador não encontrado</h3>
                            <p className="text-gray-500 mb-4">
                                O colaborador com ID "{id}" não foi encontrado.
                            </p>
                            <button
                                onClick={() => navigate('/collaborators/updates')}
                                className="btn-primary"
                            >
                                Voltar para lista
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>

            <CollaboratorForm
                context="FULL_FORM"
                collaboratorId={id || ''}
                onSubmit={handleSubmit}
                onCancel={handleBack}
            />
        </>
    );
};

export default CollaboratorEditPage;
